/**
 * Post-export diagnostics for html2canvas — copy/paste to debug WYSIWYG gaps.
 */

function cssPath(el, root) {
  if (!el || el === root) return 'captureRoot';
  const parts = [];
  let node = el;
  while (node && node !== root && parts.length < 8) {
    let part = node.tagName?.toLowerCase() || 'node';
    if (node.id) {
      part += `#${node.id}`;
    } else if (node.className && typeof node.className === 'string') {
      const cls = node.className.trim().split(/\s+/).slice(0, 2).join('.');
      if (cls) part += `.${cls}`;
    }
    parts.unshift(part);
    node = node.parentElement;
  }
  return parts.join(' > ');
}

function truncate(s, max = 120) {
  const t = String(s || '');
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

function editableTextFromHtml(documentHtml, key) {
  if (!documentHtml || typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(documentHtml, 'text/html');
  return doc.querySelector(`[data-editable="${key}"]`)?.textContent?.trim() || '';
}

function editableTextFromRoot(captureRoot, key) {
  return captureRoot?.querySelector?.(`[data-editable="${key}"]`)?.textContent?.trim() || '';
}

/** Manual QA steps for Coopers-style invites (export WYSIWYG). */
export const COOPERS_MANUAL_QA_CHECKLIST = [
  'Open Coopers invite in Media Creator.',
  'Edit headline and when_time on the artboard.',
  'Save, then Download PNG, Download HTML.',
  'Confirm PNG/HTML/viewer match: logo aspect, angled wedge, 3-up gallery, no solid yellow gutter bar.',
  'Export diagnostic: layoutSource viewer, snapshot copy matches live artboard.',
];

/** Families injected via Media Creator Google Fonts link (see MediaCreatorTab). */
export const MC_LOADED_WEB_FONTS = ['Montserrat', 'Source Sans 3'];

const GENERIC_FONTS = new Set([
  'arial',
  'helvetica',
  'sans-serif',
  'serif',
  'monospace',
  'inherit',
  'initial',
  'system-ui',
  'ui-sans-serif',
]);

function isCrossOriginSrc(src) {
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) return false;
  try {
    return new URL(src, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Inspect the artboard after export prep (inline/bake/decor) and before restore.
 */
export function collectExportDiagnostics(captureRoot, layout, meta = {}) {
  if (!captureRoot || typeof window === 'undefined') {
    return { generatedAt: new Date().toISOString(), issues: [], summary: {} };
  }

  const issues = [];
  const w = layout?.size_px?.width || 1920;
  const h = layout?.size_px?.height || 1080;

  captureRoot.querySelectorAll('img').forEach((img, i) => {
    const src = img.currentSrc || img.src || '';
    const path = cssPath(img, captureRoot);
    if (isCrossOriginSrc(src)) {
      issues.push({
        severity: 'high',
        code: 'cross_origin_image',
        path,
        detail: truncate(src),
        hint: 'Should be inlined or baked to data: URL before html2canvas.',
      });
    }
    if (!img.complete || (img.naturalWidth === 0 && src)) {
      issues.push({
        severity: 'high',
        code: 'image_not_loaded',
        path,
        detail: truncate(src),
      });
    }
    if (src && !src.startsWith('data:') && !isCrossOriginSrc(src) && img.naturalWidth > 0) {
      issues.push({
        severity: 'medium',
        code: 'non_data_image',
        path,
        detail: truncate(src),
        hint: 'Same-origin URL may still taint canvas if headers block readback.',
      });
    }
    if (img.getAttribute('data-mc-export-id') == null && i < 20) {
      /* optional noise — skip */
    }
  });

  const walk = captureRoot.querySelectorAll('*');
  walk.forEach((el) => {
    if (!(el instanceof Element)) return;
    let style;
    try {
      style = window.getComputedStyle(el);
    } catch {
      return;
    }
    const path = cssPath(el, captureRoot);
    const clip = style.clipPath;
    if (
      clip
      && clip !== 'none'
      && !path.includes('mc-export-decor')
      && !el.closest?.('.mc-copy, .mc-gallery, figure.mc-photo')
    ) {
      issues.push({
        severity: 'medium',
        code: 'clip_path',
        path,
        detail: truncate(clip),
        hint: 'html2canvas often ignores clip-path; use materializeExportDecorations SVG fallback.',
      });
    }
    const bg = style.backgroundImage;
    if (bg && bg !== 'none' && /url\(/i.test(bg) && !/url\(["']?data:/i.test(bg)) {
      if (isCrossOriginSrc(bg.match(/url\(["']?([^"')]+)/i)?.[1] || '')) {
        issues.push({
          severity: 'high',
          code: 'cross_origin_background',
          path,
          detail: truncate(bg),
        });
      }
    }
    const rect = el.getBoundingClientRect();
    if (
      rect.width < 1
      && rect.height < 1
      && style.display !== 'none'
      && style.visibility !== 'hidden'
      && (el.textContent?.trim() || el.querySelector('img'))
    ) {
      issues.push({
        severity: 'low',
        code: 'zero_size_visible',
        path,
        detail: `box ${Math.round(rect.width)}×${Math.round(rect.height)}`,
      });
    }
  });

  if (captureRoot.querySelector('iframe, video, object')) {
    issues.push({
      severity: 'high',
      code: 'embedded_media',
      path: cssPath(captureRoot.querySelector('iframe, video, object'), captureRoot),
      hint: 'html2canvas does not capture iframe/video content.',
    });
  }

  const decorMaterialized = captureRoot.querySelectorAll('[data-mc-export-decor]').length;
  const fontFamilies = new Set();
  captureRoot.querySelectorAll('h1,h2,h3,h4,p,span,div').forEach((el) => {
    const ff = window.getComputedStyle(el).fontFamily;
    if (ff) fontFamilies.add(ff.split(',')[0].replace(/['"]/g, '').trim());
  });

  const fontStatus = [];
  if (document.fonts?.forEach) {
    document.fonts.forEach((face) => {
      fontStatus.push({ family: face.family, status: face.status });
    });
  }

  const fontFamiliesList = [...fontFamilies];
  const fontLoadChecks = {};
  if (document.fonts?.check) {
    fontFamiliesList.forEach((fam) => {
      fontLoadChecks[fam] =
        document.fonts.check(`16px "${fam}"`)
        || document.fonts.check(`16px ${fam}`);
    });
  }

  fontFamiliesList.forEach((fam) => {
    const key = fam.toLowerCase();
    if (GENERIC_FONTS.has(key)) return;
    const loadedByApp = MC_LOADED_WEB_FONTS.some(
      (w) => key === w.toLowerCase() || key.startsWith(`${w.toLowerCase()} `),
    );
    if (!loadedByApp) {
      issues.push({
        severity: 'medium',
        code: 'font_family_not_in_app_stylesheet',
        path: 'computed font-family on artboard',
        detail: fam,
        hint: `Media Creator loads ${MC_LOADED_WEB_FONTS.join(' and ')}. Update document_css (e.g. Source Sans 3, not Source Sans Pro).`,
      });
    } else if (fontLoadChecks[fam] === false) {
      issues.push({
        severity: 'high',
        code: 'web_font_not_resolved',
        path: 'document.fonts',
        detail: fam,
        hint: 'Font name in CSS but browser has not resolved it — re-export after fonts load, or fix family name/weight.',
      });
    }
  });

  const snapshotHeadline = editableTextFromHtml(layout?.document_html, 'headline');
  const snapshotWhenTime = editableTextFromHtml(layout?.document_html, 'when_time');
  const liveHeadline = editableTextFromRoot(captureRoot, 'headline');
  const liveWhenTime = editableTextFromRoot(captureRoot, 'when_time');
  if (liveHeadline && snapshotHeadline && liveHeadline !== snapshotHeadline) {
    issues.push({
      severity: 'high',
      code: 'snapshot_headline_mismatch',
      path: '[data-editable=headline]',
      detail: `live="${truncate(liveHeadline, 60)}" snapshot="${truncate(snapshotHeadline, 60)}"`,
      hint: 'Export layout snapshot may be stale — re-export after editing copy on the artboard.',
    });
  }
  if (liveWhenTime && snapshotWhenTime && liveWhenTime !== snapshotWhenTime) {
    issues.push({
      severity: 'high',
      code: 'snapshot_when_time_mismatch',
      path: '[data-editable=when_time]',
      detail: `live="${truncate(liveWhenTime, 40)}" snapshot="${truncate(snapshotWhenTime, 40)}"`,
      hint: 'Save or export again so layout.document_html matches the viewer.',
    });
  }

  const cssText = layout?.document_css || '';
  if (/source\s+sans\s+pro/i.test(cssText)) {
    issues.push({
      severity: 'medium',
      code: 'document_css_font_name_mismatch',
      path: 'layout.document_css',
      detail: 'Source Sans Pro',
      hint: 'Use Source Sans 3 to match the Google Fonts link on Media Creator.',
    });
  }

  const summary = {
    exportMode: meta.exportMode || 'canvas',
    layoutSource: meta.layoutSource || 'layout',
    scale: meta.scale,
    snapshotHeadline: snapshotHeadline || null,
    snapshotWhenTime: snapshotWhenTime || null,
    liveHeadline: liveHeadline || null,
    liveWhenTime: liveWhenTime || null,
    logoTransform: meta.logoExport?.logoTransform ?? null,
    logoVisualBox: meta.logoExport?.logoVisualBox ?? null,
    logoBakeSize: meta.logoExport?.logoBakeSize ?? null,
    stagePx: { width: w, height: h },
    imageCount: captureRoot.querySelectorAll('img').length,
    dataUrlImageCount: [...captureRoot.querySelectorAll('img')].filter((i) =>
      (i.src || '').startsWith('data:'),
    ).length,
    decorMaterialized,
    issueCount: issues.length,
    highSeverity: issues.filter((x) => x.severity === 'high').length,
    fontFamilies: fontFamiliesList.slice(0, 12),
    fontLoadChecks,
    appWebFonts: MC_LOADED_WEB_FONTS,
    fontsLoaded: fontStatus.filter((f) => f.status === 'loaded').length,
    fontsTotal: fontStatus.length,
    compositionPreset: layout?.preset,
    renderMode: layout?.render_mode,
  };

  return {
    generatedAt: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    summary,
    issues: issues.slice(0, 80),
  };
}

const PARITY_TARGETS = [
  ['logo', 'img[data-logo-slot], img.mc-brand'],
  ['headline', '[data-editable="headline"]'],
  ['when_time', '[data-editable="when_time"]'],
  ['copy', '.mc-copy'],
  ['gallery', '.mc-gallery'],
];

export const PARITY_TOLERANCE_PX = 2;

function relativeRect(el, originRect, divisor) {
  const r = el.getBoundingClientRect();
  const d = divisor > 0 ? divisor : 1;
  return {
    x: Math.round(((r.left - originRect.left) / d) * 10) / 10,
    y: Math.round(((r.top - originRect.top) / d) * 10) / 10,
    w: Math.round((r.width / d) * 10) / 10,
    h: Math.round((r.height / d) * 10) / 10,
  };
}

/** Element boxes in artboard px (unscaled) relative to .mc-html-stage-inner. */
export function measureArtboardRects(stageEl, scale = 1) {
  if (!stageEl) return {};
  const origin = stageEl.getBoundingClientRect();
  const out = {};
  PARITY_TARGETS.forEach(([key, sel]) => {
    const el = stageEl.querySelector(sel);
    if (el) out[key] = relativeRect(el, origin, scale);
  });
  return out;
}

/** Compare two rect maps; returns rows with max edge delta. */
export function compareRectMaps(live, render, tolerance = PARITY_TOLERANCE_PX) {
  const keys = new Set([...Object.keys(live || {}), ...Object.keys(render || {})]);
  return [...keys].map((key) => {
    const a = live?.[key];
    const b = render?.[key];
    if (!a || !b) return { key, live: a || null, render: b || null, delta: null, ok: false };
    const delta = Math.max(
      Math.abs(a.x - b.x),
      Math.abs(a.y - b.y),
      Math.abs(a.w - b.w),
      Math.abs(a.h - b.h),
    );
    return { key, live: a, render: b, delta: Math.round(delta * 10) / 10, ok: delta <= tolerance };
  });
}

function waitIframeLoad(iframe) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('parity iframe timeout')), 15000);
    iframe.onload = () => {
      clearTimeout(t);
      resolve();
    };
  });
}

/**
 * Render the export document in a hidden 1:1 iframe and compare key boxes with the live artboard.
 * @returns {Promise<{ rows: object[] }>}
 */
export async function probeLayoutParity(liveArtboard, renderDocHtml, layout) {
  const w = layout?.size_px?.width || 1920;
  const h = layout?.size_px?.height || 1080;
  const liveStage = liveArtboard?.querySelector?.('.mc-html-stage-inner');
  if (!liveStage) return { rows: [] };
  const liveScale = liveStage.getBoundingClientRect().width / w;
  const live = measureArtboardRects(liveStage, liveScale);

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = `position:fixed;left:-${w + 100}px;top:0;width:${w}px;height:${h}px;`
    + 'border:0;opacity:0;pointer-events:none;';
  const loaded = waitIframeLoad(iframe);
  iframe.srcdoc = renderDocHtml;
  document.body.appendChild(iframe);
  try {
    await loaded;
    const idoc = iframe.contentDocument;
    await idoc?.fonts?.ready;
    await Promise.all(
      [...(idoc?.images || [])].map((img) => img.decode?.().catch(() => undefined)),
    );
    const stage = idoc?.querySelector('.mc-html-stage-inner');
    const render = measureArtboardRects(stage, 1);
    return { rows: compareRectMaps(live, render) };
  } finally {
    iframe.remove();
  }
}

/** Diagnostic for the foreignObject (browser-rendered) PNG path. */
export function collectForeignObjectDiagnostics(layout, meta = {}) {
  const w = layout?.size_px?.width || 1920;
  const h = layout?.size_px?.height || 1080;
  const issues = [];
  if (meta.fontError) {
    issues.push({
      severity: 'high',
      code: 'font_embed_failed',
      path: 'Google Fonts',
      detail: meta.fontError,
      hint: 'PNG used fallback fonts. Check network access to fonts.googleapis.com and retry.',
    });
  }
  const rows = meta.parity?.rows || [];
  rows.filter((r) => !r.ok).forEach((r) => {
    issues.push({
      severity: r.delta == null ? 'medium' : 'high',
      code: 'parity_rect_mismatch',
      path: r.key,
      detail: `live=${JSON.stringify(r.live)} export=${JSON.stringify(r.render)} delta=${r.delta}`,
      hint: 'Export document lays out differently from the editor artboard.',
    });
  });
  if (meta.parity?.error) {
    issues.push({ severity: 'low', code: 'parity_probe_failed', path: 'probe', detail: meta.parity.error });
  }

  const summary = {
    exportMode: meta.exportMode || 'foreignObject',
    layoutSource: 'viewer',
    scale: meta.scale,
    stagePx: { width: w, height: h },
    snapshotHeadline: editableTextFromHtml(layout?.document_html, 'headline') || null,
    snapshotWhenTime: editableTextFromHtml(layout?.document_html, 'when_time') || null,
    imagesInlined: meta.imagesInlined ?? 0,
    fontsEmbedded: meta.fontsEmbedded || [],
    viewportUnitsFrozen: meta.cssStats?.viewportUnitsFrozen ?? 0,
    mediaQueriesResolved: meta.cssStats?.mediaQueriesResolved ?? 0,
    svgBytes: meta.svgBytes ?? null,
    parity: rows.map((r) => ({ key: r.key, delta: r.delta, ok: r.ok })),
    fallbackReason: meta.fallbackReason || null,
    issueCount: issues.length,
    highSeverity: issues.filter((x) => x.severity === 'high').length,
    compositionPreset: layout?.preset,
    renderMode: layout?.render_mode,
  };
  return {
    generatedAt: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    summary,
    issues,
  };
}

export function formatExportDiagnosticReport(diag) {
  const lines = [
    'Media Creator — PNG export diagnostic',
    `generated: ${diag.generatedAt}`,
    '',
    'Summary:',
    JSON.stringify(diag.summary, null, 2),
    '',
  ];
  if (!diag.issues?.length) {
    lines.push('Issues: none flagged.');
  } else {
    lines.push(`Issues (${diag.issues.length}):`);
    diag.issues.forEach((item, i) => {
      lines.push(
        `${i + 1}. [${item.severity}] ${item.code} @ ${item.path}`,
      );
      if (item.detail) lines.push(`   detail: ${item.detail}`);
      if (item.hint) lines.push(`   hint: ${item.hint}`);
    });
  }
  lines.push('');
  lines.push('Paste this report when asking for export WYSIWYG fixes.');
  return lines.join('\n');
}

export async function copyExportDiagnosticReport(diag) {
  const text = formatExportDiagnosticReport(diag);
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return text;
  }
  return text;
}
